<template>
  <div class="insight-card">
    <div class="title">{{ title }}</div>
    <div class="grid">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="item"
        :class="{ 'is-new': item.isNew }"
      >
        <div class="label">
          <span>{{ item.label }}</span>
          <a-tooltip v-if="item.tooltip">
            <template #title>{{ item.tooltip }}</template>
            <QuestionCircleFilled class="icon" />
          </a-tooltip>
        </div>
        <div class="value" :class="{ 'value--warn': item.warn, 'value--good': item.good }">
          <template v-if="isValueList(item)">
            <span class="value-list-text">{{ getVisibleValueText(item) }}</span>
            <a-button v-if="shouldShowMore(item)" type="link" @click="openValueModal(item)">
              查看更多
              <RightOutlined class="more-icon" />
            </a-button>
          </template>
          <template v-else-if="isClickableItem(item)">
            <span
              class="value-clickable risk-value"
              :style="{ color: item.valueColor }"
              @click="handleItemClick(item)"
            >
              {{ item.value || '--' }}
              <RightOutlined v-if="item.suffixIcon" class="risk-value-icon" />
            </span>
          </template>
          <template v-else-if="shouldShowValueModal(item)">
            <span class="value-clickable" @click="openValueModal(item)">
              {{ item.value || '--' }}
            </span>
          </template>
          <template v-else>
            <span class="value-text">{{ item.value || '--' }}</span>
          </template>
        </div>
      </div>
    </div>
    <div class="grid">
      <div v-if="medicines.length" class="medicine-wrap">
        <div class="medicine-title" :class="{ 'is-new': currentMedicationNew }">当前用药</div>
        <div class="medicine-list">
          <a-tooltip v-for="(medicine, idx) in visibleMedicines" :key="idx" :title="medicine">
            <span class="medicine-item">{{ medicine }}</span>
          </a-tooltip>
          <a-button v-if="showMoreButton" type="link" @click="openMedicineModal">
            查看更多
            <RightOutlined class="more-icon" />
          </a-button>
        </div>
      </div>
      <div class="medicine-wrap" v-if="props.showHistoricalMedications">
        <div class="medicine-title" :class="{ 'is-new': historicalMedicationsNew }">历史用药</div>
        <div class="medicine-list" v-if="historicalMedications.length">
          <a-tooltip
            v-for="(medicine, idx) in visibleHistoryMedicines"
            :key="idx"
            :title="medicine.drugName"
          >
            <div class="medicine-item">{{ medicine.drugName }}</div>
          </a-tooltip>
          <a-button type="link" @click="openHistoryModal">
            查看更多
            <RightOutlined class="more-icon" />
          </a-button>
        </div>
        <div class="medicine-list" v-else>
          <span class="medicine-item">无历史用药记录</span>
        </div>
      </div>
    </div>

    <a-modal
      v-model:visible="medicineModalVisible"
      title="当前用药"
      :footer="null"
      :maskClosable="true"
      :destroyOnClose="true"
      width="520px"
      centered
      wrapClassName="insight-card-medicine-modal"
    >
      <div class="medicine-modal-list">
        <div v-for="(medicine, idx) in medicines" :key="idx" class="medicine-modal-item">
          {{ medicine }}
        </div>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="historyModalVisible"
      title="历史用药"
      :footer="null"
      :maskClosable="true"
      :destroyOnClose="true"
      width="472px"
      centered
      wrapClassName="insight-card-history-modal"
    >
      <div class="history-modal-list">
        <div v-for="(medicine, idx) in historicalMedications" :key="idx" class="history-modal-item">
          <div class="history-modal-item__name-row">
            <span class="history-modal-item__name">{{ medicine.drugName }}</span>
            <span v-if="medicine.returnedFlag" class="history-returned-tag">退货</span>
          </div>
          <div v-if="medicine.showTime" class="history-modal-item__time">
            {{ formatHistoryTimeRange(medicine) }}
          </div>
        </div>
      </div>
    </a-modal>

    <a-modal
      v-model:visible="valueModalVisible"
      :title="activeValueItem.modalTitle || activeValueItem.label"
      :footer="null"
      :maskClosable="true"
      :destroyOnClose="true"
      width="520px"
      centered
      wrapClassName="insight-card-value-modal"
    >
      <div v-if="isValueList(activeValueItem)" class="value-modal-list">
        <div
          v-for="(valueItem, valueIndex) in activeValueItem.values"
          :key="valueIndex"
          class="value-modal-item"
        >
          {{ valueItem }}
        </div>
      </div>
      <div v-else class="value-modal-content">{{ activeValueItem.modalContent || '--' }}</div>
    </a-modal>
  </div>
</template>

<script setup>
import { computed, defineProps, ref } from 'vue'
import { QuestionCircleFilled, RightOutlined } from '@ant-design/icons-vue'

const VISIBLE_VALUE_COUNT = 2

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  items: {
    type: Array,
    default: () => []
  },
  medicines: {
    type: Array,
    default: () => []
  },
  showHistoricalMedications: {
    type: Boolean,
    default: false
  },
  historicalMedications: {
    type: Array,
    default: () => []
  },
  currentMedicationNew: {
    type: Boolean,
    default: false
  },
  historicalMedicationsNew: {
    type: Boolean,
    default: false
  }
})

const medicines = computed(() => props.medicines || [])
const historicalMedications = computed(() => props.historicalMedications || [])
const medicineModalVisible = ref(false)
const historyModalVisible = ref(false)
const valueModalVisible = ref(false)
const activeValueItem = ref({})

const visibleMedicines = computed(() => medicines.value.slice(0, VISIBLE_VALUE_COUNT))

const visibleHistoryMedicines = computed(() =>
  (historicalMedications.value || []).slice(0, VISIBLE_VALUE_COUNT)
)

const showMoreButton = computed(() => medicines.value.length > VISIBLE_VALUE_COUNT)

const openMedicineModal = () => {
  medicineModalVisible.value = true
}

const openHistoryModal = () => {
  historyModalVisible.value = true
}

// 签收日期 ~ 结束日期（未退货为用药结束日期，退货为退货申请日期，后端已按规则返回）
const formatHistoryTimeRange = medicine => {
  const { startDate, endDate } = medicine || {}
  if (startDate && endDate) return `${startDate} 至 ${endDate}`
  return startDate || endDate || '--'
}

const isValueList = item => Array.isArray(item?.values) && item.values.length > 0

// 可点击条目（如投诉风险）：点击行为由外部通过 item.onClick 传入
const isClickableItem = item => Boolean(item?.clickable && typeof item?.onClick === 'function')

const handleItemClick = item => {
  if (isClickableItem(item)) item.onClick()
}

const getVisibleValues = item =>
  isValueList(item) ? item.values.slice(0, VISIBLE_VALUE_COUNT) : []

const getVisibleValueText = item => getVisibleValues(item).join('，')

const shouldShowMore = item => isValueList(item) && item.values.length > VISIBLE_VALUE_COUNT

const hasModalContent = item =>
  item?.modalContent !== undefined &&
  item?.modalContent !== null &&
  item.modalContent !== '' &&
  item.modalContent !== '--'

const shouldShowValueModal = item => !isValueList(item) && hasModalContent(item)

const openValueModal = item => {
  activeValueItem.value = item || {}
  valueModalVisible.value = true
}
</script>

<style lang="scss" scoped>
.insight-card {
  background: #f5f7ff;
  border-radius: 4px;
  padding: 14px 16px 16px;
  margin-bottom: 12px;
  :deep(.ant-btn) {
    padding: 0;
    display: block;
  }
  .title {
    position: relative;
    padding-left: 10px;
    color: #2f4867;
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
    margin-bottom: 12px;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 4px;
      width: 2px;
      height: 12px;
      border-radius: 2px;
      background: #3ea0ff;
    }
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 18px;
  }

  .item {
    min-width: 0;

    .label {
      display: flex;
      align-items: center;
      gap: 4px;
      color: #96a5be;
      font-size: 12px;
      line-height: 18px;
      margin-bottom: 6px;

      .icon {
        color: #96a5be;
        cursor: pointer;
        font-size: 13px;
      }
    }

    .value {
      color: #031d4a;
      font-size: 15px;
      line-height: 21px;
      font-weight: 500;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px;
    }

    .value--warn {
      color: #fc8801;
    }

    .value--good {
      color: #02c5a2;
    }
  }

  .item.is-new .value-list-text,
  .item.is-new .value-clickable,
  .item.is-new .value-text {
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 100%;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: #ff2b2b;
    }
  }

  .medicine-wrap {
    margin-top: 14px;

    .medicine-title {
      color: #7e91aa;
      font-size: 12px;
      line-height: 18px;
      margin-bottom: 6px;
    }

    .medicine-title.is-new::after {
      content: '';
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: #ff2b2b;
      margin-left: 2px;
      vertical-align: top;
    }

    .medicine-list {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;

      .medicine-item {
        background: transparent;
        border: 0;
        color: #031d4a;
        font-size: 15px;
        line-height: 20px;
        font-style: normal;
        font-weight: 500;
        padding: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

.medicine-modal-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// 退货标记
.history-returned-tag {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px 6px;
  background: #f0f5ff;
  border-radius: 4px;
  color: #566f8d;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
}

// 历史用药弹窗列表
.history-modal-list {
  display: flex;
  flex-direction: column;
}

.history-modal-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;

  & + .history-modal-item {
    border-top: 1px solid #efefef;
  }

  &__name-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__name {
    color: #333333;
    font-size: 16px;
    line-height: 22px;
    font-style: normal;
    font-weight: 400;
    word-break: break-all;
  }

  &__time {
    color: #666666;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
  }
}

.medicine-modal-item,
.value-modal-item,
.value-modal-content {
  color: #031d4a;
  font-size: 15px;
  line-height: 20px;
  font-style: normal;
  font-weight: 500;
  word-break: break-all;
}

.value-modal-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.value-list-text {
  min-width: 0;
  word-break: break-all;
}

.value-clickable {
  min-width: 0;
  cursor: pointer;
}

// 风险值条目：彩色文字 + 下拉箭头
.value-clickable.risk-value {
  display: inline-flex;
  align-items: center;
  gap: 2px;

  .risk-value-icon {
    font-size: 12px;
  }
}

.more-icon {
  margin-left: 0;
}
</style>

<style lang="scss">
// 历史用药弹窗内容区
.insight-card-history-modal {
  .ant-modal-body {
    padding: 0 24px;
  }
}
</style>
